// v10/src/components/FlowScreen.js
// Flow Mode — pantalla completa, un ítem a la vez, con trampas y Valentina
// + Registro de anomalías: observación o squawk (con AOG)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, AlertTriangle, Volume2 } from 'lucide-react';
import api from '../services/apiService';
import { useTranslation } from 'react-i18next';
import { speakText, stopSpeech } from '../utils/speechHelper';
import FlowResults from './FlowResults';

const FlowScreen = ({ aircraft, inspectionId, currentUser, darkMode, orgFeatures, onClose }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(() => {
    try { return parseInt(sessionStorage.getItem('condor360_flow_index') || '0', 10); } catch { return 0; }
  });
  const [flowConfig, setFlowConfig] = useState({});
  const [trapIdsEncoded, setTrapIdsEncoded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Timer por ítem
  const [itemStartTime, setItemStartTime] = useState(null);
  const [events, setEvents] = useState([]);
  const [flowStartTime] = useState(Date.now());

  // Estado del flujo
  const [completed, setCompleted] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [restartWarning, setRestartWarning] = useState(false);

  // Hobbs input
  const [hobbsValue, setHobbsValue] = useState('');
  const [hobbsError, setHobbsError] = useState('');
  const savedHobbsRef = useRef(null); // persiste el valor aunque se limpie el state

  // Oil input
  const [oilValue, setOilValue] = useState('');
  const [oilError, setOilError] = useState('');
  const savedOilRef = useRef(null);

  // Fuel input
  const [fuelLeftValue,  setFuelLeftValue]  = useState('');
  const [fuelRightValue, setFuelRightValue] = useState('');
  const [fuelError, setFuelError] = useState('');
  const savedFuelRef = useRef({ left: null, right: null, unit: null });

  // Swipe
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Slow response timer
  const slowTimerRef = useRef(null);
  const [slowWarning, setSlowWarning] = useState(false);

  // ── Anomalía ──────────────────────────────────────────────────────
  const [flowAnomalies, setFlowAnomalies]         = useState([]); // track local para el PDF
  const [showAnomalyModal, setShowAnomalyModal]   = useState(false);
  const [anomalyText, setAnomalyText]             = useState('');
  const [anomalyStep, setAnomalyStep]             = useState('input'); // 'input' | 'aog'
  const [savingAnomaly, setSavingAnomaly]         = useState(false);
  const [anomalyToast, setAnomalyToast]           = useState(null); // { msg, color }
  const anomalyInputRef                           = useRef(null);
  const photoInputRef                             = useRef(null);
  const [photoPreview, setPhotoPreview]           = useState(null);  // data URL para preview
  const [photoBase64, setPhotoBase64]             = useState(null);  // base64 puro para upload
  const [photoMediaType, setPhotoMediaType]       = useState('image/jpeg');

  const showToast = (msg, color = '#22c55e') => {
    setAnomalyToast({ msg, color });
    setTimeout(() => setAnomalyToast(null), 3000);
  };

  const openAnomalyModal = () => {
    setAnomalyText('');
    setAnomalyStep('input');
    setPhotoPreview(null);
    setPhotoBase64(null);
    setShowAnomalyModal(true);
    // Pausar slow timer mientras el modal está abierto
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    setTimeout(() => anomalyInputRef.current?.focus(), 200);
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoMediaType(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPhotoPreview(dataUrl);
      // Extraer solo el base64 puro (sin el prefijo data:...;base64,)
      setPhotoBase64(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  // Subir foto después de crear la anomalía
  const uploadAnomalyPhoto = async (anomalyId) => {
    if (!photoBase64 || !anomalyId) return;
    try {
      await api.post('/api/images/flow-anomaly/upload', {
        image:      photoBase64,
        mediaType:  photoMediaType,
        anomaly_id: anomalyId,
      });
    } catch (err) {
      console.error('[FlowAnomaly] photo upload error:', err?.response?.data || err.message);
    }
  };

  const closeAnomalyModal = () => {
    setShowAnomalyModal(false);
    setAnomalyText('');
    setAnomalyStep('input');
    setPhotoPreview(null);
    setPhotoBase64(null);
  };

  // Guardar como observación
  const handleSaveObservation = async () => {
    if (!anomalyText.trim()) return;
    setSavingAnomaly(true);
    try {
      const res = await api.post('/api/flow/anomaly', {
        inspection_id: inspectionId,
        aircraft_id:   aircraft.id,
        text:          anomalyText.trim(),
        type:          'observation',
        reported_by:   currentUser?.id,
      });
      // Subir foto si hay (fire-and-forget, no bloquea el flujo)
      const anomalyId = res?.data?.data?.anomaly_id;
      if (photoBase64 && anomalyId) uploadAnomalyPhoto(anomalyId).catch(() => {});
      setFlowAnomalies(prev => [...prev, { text: anomalyText.trim(), type: 'observation', is_aog: false }]);
      closeAnomalyModal();
      showToast('📝 Observación registrada');
    } catch (_) {
      setFlowAnomalies(prev => [...prev, { text: anomalyText.trim(), type: 'observation', is_aog: false }]);
      closeAnomalyModal();
      showToast('📝 Observación registrada (sin conexión)');
    } finally { setSavingAnomaly(false); }
  };

  // Al tocar "Reportar como squawk" — pide AOG
  const handleGoToAog = () => {
    if (!anomalyText.trim()) return;
    setAnomalyStep('aog');
  };

  // Guardar como squawk (con o sin AOG) — vía /api/flow/anomaly
  const handleSaveSquawk = async (isAog) => {
    setSavingAnomaly(true);
    try {
      const res = await api.post('/api/flow/anomaly', {
        inspection_id: inspectionId,
        aircraft_id:   aircraft.id,
        text:          anomalyText.trim(),
        type:          'squawk',
        is_aog:        isAog,
      });
      // Subir foto si hay (fire-and-forget)
      const anomalyId = res?.data?.data?.anomaly_id;
      if (photoBase64 && anomalyId) uploadAnomalyPhoto(anomalyId).catch(() => {});
      setFlowAnomalies(prev => [...prev, { text: anomalyText.trim(), type: 'squawk', is_aog: isAog }]);
      // Notificar al AircraftSelector para que refresque el estado AOG
      window.dispatchEvent(new CustomEvent('condor360:squawk-updated'));
      closeAnomalyModal();
      showToast(
        isAog ? '⛔ Squawk AOG creado — avión bloqueado' : '🔴 Squawk creado',
        isAog ? '#ef4444' : '#f59e0b'
      );
    } catch (err) {
      console.error('[FlowAnomaly] squawk error:', err?.response?.data || err.message);
      closeAnomalyModal();
      showToast('Error al registrar squawk — revisá la conexión', '#ef4444');
    } finally { setSavingAnomaly(false); }
  };

  // ── Persistir currentIndex en sessionStorage para sobrevivir refresh ──
  useEffect(() => {
    try { sessionStorage.setItem('condor360_flow_index', String(currentIndex)); } catch (_) {}
  }, [currentIndex]);

  // ── Cargar checklist en formato flow ──────────────────────────────
  useEffect(() => {
    const fetchFlow = async () => {
      try {
        const res = await api.get(`/api/flow/checklist/${aircraft.id}`);
        if (res.data.success) {
          setItems(res.data.data.items);
          setFlowConfig(res.data.data.flow_config || {});
          setTrapIdsEncoded(res.data._trap_ids || null);
          setItemStartTime(Date.now());
        } else {
          setError('No se pudo cargar el checklist');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error al cargar Flow Mode');
      } finally {
        setLoading(false);
      }
    };
    fetchFlow();
    return () => { stopSpeech(); if (slowTimerRef.current) clearTimeout(slowTimerRef.current); };
  }, [aircraft.id]);

  // ── Registrar evento ──────────────────────────────────────────────
  const registerEvent = useCallback(async (eventType, item) => {
    const responseTime = itemStartTime ? Date.now() - itemStartTime : null;
    try {
      const res = await api.post('/api/flow/event', {
        inspection_id:    inspectionId,
        item_id:          item?.id || null,
        item_label:       item?.label || '',
        event_type:       eventType,
        response_time_ms: responseTime,
      });
      setEvents(prev => [...prev, { item_id: item?.id, item_label: item?.label, event_type: eventType, response_time_ms: responseTime }]);
      if (res.data.restart) setRestartWarning(true);
      return res.data;
    } catch (_) { return null; }
  }, [inspectionId, itemStartTime]);

  // ── Valentina: hablar ítems críticos ──────────────────────────────
  useEffect(() => {
    if (items.length === 0 || currentIndex >= items.length) return;
    const item = items[currentIndex];

    setSlowWarning(false);
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);

    const threshold = flowConfig.slow_response_threshold_ms || 8000;
    const audioCtx  = { aircraftId: aircraft.id, sectionName: item.section, itemText: item.label };

    if (item?.item_type !== 'hobbs' && item?.item_type !== 'oil' && item?.item_type !== 'fuel') {
      slowTimerRef.current = setTimeout(() => {
        setSlowWarning(true);
        registerEvent('slow_response', item);
        speakText(item.label, () => {}, audioCtx);
      }, threshold);
    }

    if (item.is_critical) speakText(item.label, () => {}, audioCtx);

    setItemStartTime(Date.now());
    setHobbsValue('');
    setHobbsError('');
    setOilValue('');
    setOilError('');
    setFuelLeftValue('');
    setFuelRightValue('');
    setFuelError('');

    return () => { if (slowTimerRef.current) clearTimeout(slowTimerRef.current); };
  }, [currentIndex, items, flowConfig, registerEvent]);

  // ── Confirmar ítem ────────────────────────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (showAnomalyModal) return; // no avanzar con modal abierto
    if (currentIndex >= items.length) return;
    const item = items[currentIndex];

    if (item?.item_type === 'hobbs') {
      const val = parseFloat(hobbsValue);
      if (!hobbsValue || isNaN(val) || val <= 0) { setHobbsError(t('hobbs_required',{ns:'common'})); return; }
      try {
        const API    = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const stored = localStorage.getItem('condor360_current_user') || '{}';
        const token  = JSON.parse(stored).token || '';
        await fetch(`${API}/api/aircraft/${aircraft.id}/hobbs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ hobbs_start: val, hobbs_end: val, inspection_id: inspectionId }),
        });
      } catch (_) {}
      savedHobbsRef.current = val;
      setHobbsValue(''); setHobbsError('');
    }

    if (item?.item_type === 'oil') {
      const val = parseFloat(oilValue);
      if (!oilValue || isNaN(val) || val <= 0 || val >= 99) {
        setOilError(t('oil_required', { ns: 'common' }) || 'Ingresá un valor entre 0 y 99 qt');
        return;
      }
      try {
        const API    = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const stored = localStorage.getItem('condor360_current_user') || '{}';
        const token  = JSON.parse(stored).token || '';
        await fetch(`${API}/api/aircraft/${aircraft.id}/oil`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ oil_start_qts: val, inspection_id: inspectionId }),
        });
      } catch (_) {}
      savedOilRef.current = val;
      setOilValue(''); setOilError('');
    }

    if (item?.item_type === 'fuel') {
      const singleTank = aircraft?.single_tank === true;
      const fuelSide   = singleTank ? 'left' : (item?.fuel_side || 'both');
      const showLeft   = fuelSide === 'left'  || fuelSide === 'both';
      const showRight  = !singleTank && (fuelSide === 'right' || fuelSide === 'both');
      const capLeft    = aircraft?.fuel_capacity_left  != null ? parseFloat(aircraft.fuel_capacity_left)  : null;
      const capRight   = aircraft?.fuel_capacity_right != null ? parseFloat(aircraft.fuel_capacity_right) : null;
      const fuelUnit   = aircraft?.fuel_unit || 'L';

      const left  = showLeft  && fuelLeftValue  !== '' ? parseFloat(fuelLeftValue)  : null;
      const right = showRight && fuelRightValue !== '' ? parseFloat(fuelRightValue) : null;

      if ((showLeft && left == null) || (showRight && right == null)) {
        setFuelError('Completá el valor del combustible'); return;
      }
      if (left != null && (isNaN(left) || left < 0)) {
        setFuelError('Valor inválido para el tanque izquierdo'); return;
      }
      if (right != null && (isNaN(right) || right < 0)) {
        setFuelError('Valor inválido para el tanque derecho'); return;
      }
      if (left != null && capLeft != null && left > capLeft) {
        setFuelError(`Excede la capacidad del tanque izquierdo (${capLeft} ${fuelUnit})`); return;
      }
      if (right != null && capRight != null && right > capRight) {
        setFuelError(`Excede la capacidad del tanque derecho (${capRight} ${fuelUnit})`); return;
      }

      try {
        const API    = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const stored = localStorage.getItem('condor360_current_user') || '{}';
        const token  = JSON.parse(stored).token || '';
        const body   = { inspection_id: inspectionId };
        if (left  != null) body.fuel_start_left  = left;
        if (right != null) body.fuel_start_right = right;
        await fetch(`${API}/api/aircraft/${aircraft.id}/fuel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(body),
        });
      } catch (_) {}
      savedFuelRef.current = {
        left:  left  != null ? left  : savedFuelRef.current.left,
        right: right != null ? right : savedFuelRef.current.right,
        unit:  fuelUnit,
      };
      setFuelLeftValue(''); setFuelRightValue(''); setFuelError('');
    }

    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    if (navigator.vibrate) navigator.vibrate(40);
    const result = await registerEvent('trap_confirmed', item);
    if (result?.restart && navigator.vibrate) navigator.vibrate([100, 50, 100]);
    advance();
  }, [currentIndex, items, registerEvent, hobbsValue, oilValue, fuelLeftValue, fuelRightValue, aircraft, showAnomalyModal]);

  // ── Rechazar ítem ─────────────────────────────────────────────────
  const handleReject = useCallback(async () => {
    if (showAnomalyModal) return;
    if (currentIndex >= items.length) return;
    const item = items[currentIndex];
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    if (navigator.vibrate) navigator.vibrate([20, 50, 20]);
    await registerEvent('trap_rejected', item);
    advance();
  }, [currentIndex, items, registerEvent, showAnomalyModal]);

  // ── Avanzar ───────────────────────────────────────────────────────
  const advance = useCallback(() => {
    const next = currentIndex + 1;
    if (next >= items.length) handleComplete();
    else setCurrentIndex(next);
  }, [currentIndex, items]);

  // ── Completar ─────────────────────────────────────────────────────
  const handleComplete = useCallback(async () => {
    stopSpeech();
    if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
    // Limpiar índice persistido al terminar el flow
    try { sessionStorage.removeItem('condor360_flow_index'); } catch (_) {}
    const isPreLanding  = aircraft.checklist_type === 'pre_landing';
    const hasAnomalies  = flowAnomalies.length > 0;
    const closingText   = isPreLanding
      ? 'Pre-aterrizaje completo.'
      : hasAnomalies
        ? 'Prevuelo completo. Tené en cuenta las observaciones reportadas.'
        : 'Prevuelo completo. Buen vuelo.';
    speakText(closingText, () => {}, { aircraftId: aircraft.id });
    try {
      const res = await api.post('/api/flow/complete', {
        inspection_id: inspectionId,
        _trap_ids:     trapIdsEncoded,
      });
      if (res.data.success) {
        setCompletionData({ ...res.data.data, total_time_ms: Date.now() - flowStartTime, events, anomalies: flowAnomalies, hobbs_value: savedHobbsRef.current || null, oil_value: savedOilRef.current || null, fuel_value: savedFuelRef.current || null });
      }
    } catch (_) {}
    setCompleted(true);
  }, [inspectionId, trapIdsEncoded, flowStartTime, events, aircraft, flowAnomalies]);

  // ── Reiniciar tras trampa ─────────────────────────────────────────
  const handleRestart = () => {
    setRestartWarning(false);
    setCurrentIndex(0);
    setEvents([]);
    setItemStartTime(Date.now());
  };

  // ── Swipe ─────────────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    if (showAnomalyModal) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (showAnomalyModal) return;
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
    if (dx > 0) handleConfirm();
    else handleReject();
  };

  // ── Keyboard ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (showAnomalyModal) return;
      if (completed || restartWarning) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleConfirm();
      if (e.key === 'ArrowLeft') handleReject();
      if (e.key === 'Escape') handleReject();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleConfirm, handleReject, completed, restartWarning, showAnomalyModal]);

  // ── RENDER ────────────────────────────────────────────────────────

  if (completed) {
    return (
      <FlowResults
        data={completionData}
        aircraft={aircraft}
        darkMode={darkMode}
        onClose={onClose}
        currentUser={currentUser}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#0f172a', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'20px' }}>
        <motion.div animate={{ rotate:360 }} transition={{ duration:1.5, repeat:Infinity, ease:'linear' }}
          style={{ width:'48px', height:'48px', borderRadius:'50%', border:'3px solid transparent', borderTopColor:'#818cf8', borderRightColor:'#6d28d9' }} />
        <p style={{ color:'#94a3b8', fontWeight:600, fontSize:'15px' }}>Cargando Flow Mode...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:9999, background:'#0f172a', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'20px', padding:'32px' }}>
        <AlertTriangle style={{ width:'48px', height:'48px', color:'#f59e0b' }} />
        <p style={{ color:'#f1f5f9', fontWeight:700, fontSize:'18px', textAlign:'center' }}>{error}</p>
        <motion.button onClick={onClose} whileTap={{ scale:0.95 }}
          style={{ padding:'12px 32px', background:'linear-gradient(135deg,#2563eb,#6d28d9)', color:'white', border:'none', borderRadius:'14px', fontWeight:700, fontSize:'15px', cursor:'pointer' }}>
          Volver
        </motion.button>
      </div>
    );
  }

  const item     = items[currentIndex];
  const progress = items.length > 0 ? (currentIndex / items.length) * 100 : 0;

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, background:'#0f172a', display:'flex', flexDirection:'column', touchAction:'pan-y' }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Barra de progreso ──────────────────────────────────────── */}
      <div style={{ height:'4px', background:'rgba(255,255,255,0.08)', flexShrink:0 }}>
        <motion.div
          animate={{ width:`${progress}%` }}
          transition={{ type:'spring', stiffness:120, damping:20 }}
          style={{ height:'100%', background:'linear-gradient(90deg,#818cf8,#6d28d9)', borderRadius:'0 2px 2px 0' }}
        />
      </div>

      {/* ── Header ────────────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <span style={{ fontSize:'20px' }}>🌀</span>
          <div>
            <p style={{ margin:0, fontSize:'13px', fontWeight:700, color:'#e2e8f0' }}>Flow Mode</p>
            <p style={{ margin:0, fontSize:'11px', color:'#64748b' }}>
              {aircraft.registration || aircraft.name} · {currentIndex + 1}/{items.length}
            </p>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {/* Botón observación */}
          <motion.button
            onClick={openAnomalyModal}
            whileHover={{ scale:1.05 }}
            whileTap={{ scale:0.93 }}
            style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:12, border:'1px solid rgba(245,158,11,0.35)', background:'rgba(245,158,11,0.08)', cursor:'pointer', color:'#fbbf24', fontSize:12, fontWeight:700, fontFamily:'inherit' }}>
            <span style={{ fontSize:14 }}>⚠️</span> Observación
          </motion.button>
          <motion.button onClick={onClose} whileTap={{ scale:0.9 }}
            style={{ width:'36px', height:'36px', borderRadius:'12px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#64748b' }}>
            <X style={{ width:'18px', height:'18px' }} />
          </motion.button>
        </div>
      </div>

      {/* ── Ítem central ──────────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 24px', overflow:'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity:0, x:80 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:-80 }}
            transition={{ type:'spring', stiffness:200, damping:25 }}
            style={{ textAlign:'center', maxWidth:'500px', width:'100%' }}
          >
            <p style={{ margin:'0 0 12px', fontSize:'13px', fontWeight:600, color:'#818cf8', textTransform:'uppercase', letterSpacing:'0.08em' }}>
              {item?.section || ''}
            </p>
            <h1 style={{ margin:'0 0 16px', fontSize:'28px', fontWeight:800, color:'#f1f5f9', lineHeight:1.3 }}>
              {item?.label || ''}
            </h1>

            {/* Hobbs input */}
            {item?.item_type === 'hobbs' && (
              <div style={{ marginTop:'24px', display:'flex', flexDirection:'column', gap:'8px', alignItems:'center' }}>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', width:'100%', maxWidth:'280px' }}>
                  <div style={{ flex:1, position:'relative' }}>
                    <input
                      type="number" step="0.1" min="0"
                      value={hobbsValue}
                      onChange={e => { setHobbsValue(e.target.value); setHobbsError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleConfirm()}
                      placeholder="ej: 1259.5"
                      onClick={e => e.stopPropagation()}
                      style={{ width:'100%', padding:'12px 44px 12px 16px', borderRadius:'14px', border:hobbsError?'2px solid #ef4444':'2px solid rgba(129,140,248,0.5)', background:'rgba(30,40,80,0.6)', color:'white', fontSize:'20px', fontWeight:700, fontFamily:'monospace', outline:'none', boxSizing:'border-box', textAlign:'center', WebkitAppearance:'none', MozAppearance:'textfield' }}
                    />
                    <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'13px', fontWeight:700, color:'rgba(165,180,252,0.7)', pointerEvents:'none' }}>hs</span>
                  </div>
                </div>
                {hobbsError && <p style={{ margin:0, fontSize:'12px', color:'#f87171', fontWeight:600 }}>⚠ {hobbsError}</p>}
              </div>
            )}

            {/* Oil input */}
            {item?.item_type === 'oil' && (
              <div style={{ marginTop:'24px', display:'flex', flexDirection:'column', gap:'8px', alignItems:'center' }}>
                <div style={{ display:'flex', gap:'8px', alignItems:'center', width:'100%', maxWidth:'280px' }}>
                  <div style={{ flex:1, position:'relative' }}>
                    <input
                      type="number" step="0.1" min="0" max="99"
                      value={oilValue}
                      onChange={e => { setOilValue(e.target.value); setOilError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleConfirm()}
                      placeholder={t('oil_placeholder', { ns: 'common' }) || 'ej: 7.0'}
                      onClick={e => e.stopPropagation()}
                      style={{ width:'100%', padding:'12px 44px 12px 16px', borderRadius:'14px', border:oilError?'2px solid #ef4444':'2px solid rgba(129,140,248,0.5)', background:'rgba(30,40,80,0.6)', color:'white', fontSize:'20px', fontWeight:700, fontFamily:'monospace', outline:'none', boxSizing:'border-box', textAlign:'center', WebkitAppearance:'none', MozAppearance:'textfield' }}
                    />
                    <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'13px', fontWeight:700, color:'rgba(165,180,252,0.7)', pointerEvents:'none' }}>qt</span>
                  </div>
                </div>
                {oilError && <p style={{ margin:0, fontSize:'12px', color:'#f87171', fontWeight:600 }}>⚠ {oilError}</p>}
              </div>
            )}

            {/* Fuel input — 1 o 2 tanques según single_tank / fuel_side */}
            {item?.item_type === 'fuel' && (() => {
              const singleTank = aircraft?.single_tank === true;
              const fuelSide   = singleTank ? 'left' : (item?.fuel_side || 'both');
              const showLeft   = fuelSide === 'left'  || fuelSide === 'both';
              const showRight  = !singleTank && (fuelSide === 'right' || fuelSide === 'both');
              const fuelUnit   = aircraft?.fuel_unit || 'L';
              const inputBox = (label, value, setValue) => (
                <div style={{ flex:1, position:'relative', minWidth:0 }}>
                  <input
                    type="number" step="0.1" min="0"
                    value={value}
                    onChange={e => { setValue(e.target.value); setFuelError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleConfirm()}
                    placeholder={label}
                    onClick={e => e.stopPropagation()}
                    style={{ width:'100%', padding:'12px 60px 12px 16px', borderRadius:'14px', border:fuelError?'2px solid #ef4444':'2px solid rgba(16,185,129,0.5)', background:'rgba(8,40,30,0.6)', color:'white', fontSize:'20px', fontWeight:700, fontFamily:'monospace', outline:'none', boxSizing:'border-box', textAlign:'center', WebkitAppearance:'none', MozAppearance:'textfield' }}
                  />
                  <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', fontSize:'13px', fontWeight:700, color:'rgba(110,231,183,0.85)', pointerEvents:'none' }}>{label.endsWith('izq')||label==='izq'||label.startsWith('Izq')?`${fuelUnit} izq`:label.endsWith('der')||label==='der'||label.startsWith('Der')?`${fuelUnit} der`:fuelUnit}</span>
                </div>
              );
              return (
                <div style={{ marginTop:'24px', display:'flex', flexDirection:'column', gap:'10px', alignItems:'center', width:'100%', maxWidth:'420px', margin:'24px auto 0' }}>
                  <div style={{ display:'flex', gap:'10px', alignItems:'stretch', width:'100%' }}>
                    {showLeft && inputBox(singleTank ? 'ej: 80' : 'Izq · ej: 40', fuelLeftValue, setFuelLeftValue)}
                    {showRight && inputBox('Der · ej: 40', fuelRightValue, setFuelRightValue)}
                  </div>
                  {fuelError && <p style={{ margin:0, fontSize:'12px', color:'#f87171', fontWeight:600 }}>⚠ {fuelError}</p>}
                </div>
              );
            })()}

            <AnimatePresence>
              {slowWarning && (
                <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                  style={{ marginTop:'20px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                  <Volume2 style={{ width:'16px', height:'16px', color:'#f59e0b' }} />
                  <span style={{ fontSize:'13px', color:'#f59e0b', fontWeight:600 }}>Verificá este ítem</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Botones acción ─────────────────────────────────────────── */}
      <div style={{ padding:'20px 24px 40px', display:'flex', gap:'16px', flexShrink:0 }}>
        <motion.button onClick={handleReject} whileHover={{ scale:1.02 }} whileTap={{ scale:0.95 }}
          style={{ flex:1, padding:'18px', borderRadius:'18px', border:'2px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#fca5a5', fontSize:'16px', fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
          ✗ No aplica
        </motion.button>
        <motion.button onClick={handleConfirm} whileHover={{ scale:1.02 }} whileTap={{ scale:0.95 }}
          style={{ flex:2, padding:'18px', borderRadius:'18px', border:'none', background:'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontSize:'18px', fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', boxShadow:'0 4px 20px rgba(34,197,94,0.3)', fontFamily:'inherit' }}>
          ✓ Verificado <ChevronRight style={{ width:'20px', height:'20px' }} />
        </motion.button>
      </div>

      {/* ── Hint swipe ─────────────────────────────────────────────── */}
      {currentIndex === 0 && (
        <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:1.5 }}
          style={{ position:'absolute', bottom:'12px', left:0, right:0, textAlign:'center', fontSize:'11px', color:'#475569' }}>
          ← Swipe izq: no aplica · Swipe der: verificado →
        </motion.p>
      )}

      {/* ── Toast de confirmación ──────────────────────────────────────── */}
      <AnimatePresence>
        {anomalyToast && (
          <motion.div
            initial={{ opacity:0, y:-20 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-20 }}
            style={{ position:'fixed', top:16, left:'50%', transform:'translateX(-50%)', zIndex:10002,
              background: anomalyToast.color, backdropFilter:'blur(8px)',
              padding:'10px 20px', borderRadius:20,
              display:'flex', alignItems:'center', gap:8, whiteSpace:'nowrap',
              boxShadow:'0 4px 20px rgba(0,0,0,0.3)' }}>
            <span style={{ fontSize:13, fontWeight:700, color:'white' }}>{anomalyToast.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal de observación ──────────────────────────────────────── */}
      <AnimatePresence>
        {showAnomalyModal && (
          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:10001, background:'rgba(0,0,0,0.75)', backdropFilter:'blur(6px)', display:'flex', alignItems:'flex-end', justifyContent:'center', padding:'0 0 0 0' }}
            onClick={closeAnomalyModal}
          >
            <motion.div
              initial={{ y:80, opacity:0 }} animate={{ y:0, opacity:1 }} exit={{ y:80, opacity:0 }}
              transition={{ type:'spring', stiffness:200, damping:28 }}
              style={{ width:'100%', maxWidth:480, background:'#1e293b', borderRadius:'24px 24px 0 0', padding:'24px 20px 40px', border:'1px solid rgba(255,255,255,0.08)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Handle */}
              <div style={{ width:40, height:4, borderRadius:2, background:'rgba(255,255,255,0.15)', margin:'0 auto 20px' }} />

              {anomalyStep === 'input' ? (
                <>
                  <h3 style={{ margin:'0 0 16px', fontSize:18, fontWeight:800, color:'#f1f5f9', textAlign:'center' }}>
                    ⚠️ Anotar observación
                  </h3>
                  <textarea
                    ref={anomalyInputRef}
                    value={anomalyText}
                    onChange={e => setAnomalyText(e.target.value)}
                    placeholder="Describí la observación o defecto detectado..."
                    rows={3}
                    style={{ width:'100%', padding:'14px 16px', borderRadius:14, border:'1.5px solid rgba(129,140,248,0.4)', background:'rgba(15,23,42,0.8)', color:'#f1f5f9', fontSize:16, fontFamily:'inherit', outline:'none', resize:'none', boxSizing:'border-box', marginBottom:12 }}
                  />

                  {/* ── Foto opcional ── */}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoSelect}
                    style={{ display:'none' }}
                  />
                  {photoPreview ? (
                    <div style={{ position:'relative', marginBottom:12 }}>
                      <img src={photoPreview} alt="preview"
                        style={{ width:'100%', maxHeight:140, objectFit:'cover', borderRadius:12, border:'1px solid rgba(129,140,248,0.3)' }} />
                      <button
                        onClick={() => { setPhotoPreview(null); setPhotoBase64(null); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                        style={{ position:'absolute', top:6, right:6, width:24, height:24, borderRadius:'50%', background:'rgba(0,0,0,0.6)', border:'none', color:'white', fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>
                        ✕
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      style={{ width:'100%', padding:'10px', borderRadius:12, border:'1px dashed rgba(129,140,248,0.3)', background:'transparent', color:'rgba(148,163,184,0.7)', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', marginBottom:4, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      <span style={{ fontSize:16 }}>📷</span> Agregar foto (opcional)
                    </button>
                  )}

                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <motion.button
                      onClick={handleSaveObservation}
                      disabled={!anomalyText.trim() || savingAnomaly}
                      whileTap={{ scale:0.97 }}
                      style={{ width:'100%', padding:'15px', borderRadius:16, border:'1.5px solid rgba(99,102,241,0.4)', background:'rgba(99,102,241,0.1)', color: anomalyText.trim() ? '#a5b4fc' : '#475569', fontSize:15, fontWeight:700, cursor: anomalyText.trim() ? 'pointer' : 'not-allowed', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      <span style={{ fontSize:18 }}>📝</span> Solo observación
                    </motion.button>
                    <motion.button
                      onClick={handleGoToAog}
                      disabled={!anomalyText.trim() || savingAnomaly}
                      whileTap={{ scale:0.97 }}
                      style={{ width:'100%', padding:'15px', borderRadius:16, border:'1.5px solid rgba(239,68,68,0.4)', background:'rgba(239,68,68,0.1)', color: anomalyText.trim() ? '#fca5a5' : '#475569', fontSize:15, fontWeight:700, cursor: anomalyText.trim() ? 'pointer' : 'not-allowed', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      <span style={{ fontSize:18 }}>🔴</span> Reportar como squawk
                    </motion.button>
                    <button onClick={closeAnomalyModal}
                      style={{ background:'transparent', border:'none', color:'#475569', fontSize:13, fontWeight:600, cursor:'pointer', padding:'8px', fontFamily:'inherit' }}>
                      Cancelar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ margin:'0 0 8px', fontSize:18, fontWeight:800, color:'#f1f5f9', textAlign:'center' }}>
                    🔴 ¿Es AOG?
                  </h3>
                  <p style={{ margin:'0 0 20px', fontSize:13, color:'#64748b', textAlign:'center', lineHeight:1.5 }}>
                    "{anomalyText}"
                  </p>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    <motion.button
                      onClick={() => handleSaveSquawk(false)}
                      disabled={savingAnomaly}
                      whileTap={{ scale:0.97 }}
                      style={{ width:'100%', padding:'15px', borderRadius:16, border:'1.5px solid rgba(245,158,11,0.4)', background:'rgba(245,158,11,0.1)', color:'#fbbf24', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      ✈️ No AOG — puede volar
                    </motion.button>
                    <motion.button
                      onClick={() => handleSaveSquawk(true)}
                      disabled={savingAnomaly}
                      whileTap={{ scale:0.97 }}
                      style={{ width:'100%', padding:'15px', borderRadius:16, border:'none', background:'linear-gradient(135deg,#ef4444,#dc2626)', color:'white', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:'0 4px 16px rgba(239,68,68,0.35)' }}>
                      ⛔ AOG — bloquear avión
                    </motion.button>
                    <button onClick={() => setAnomalyStep('input')}
                      style={{ background:'transparent', border:'none', color:'#475569', fontSize:13, fontWeight:600, cursor:'pointer', padding:'8px', fontFamily:'inherit' }}>
                      ← Volver
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal restart por trampa ───────────────────────────────── */}
      <AnimatePresence>
        {restartWarning && (
          <motion.div
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', padding:'32px' }}>
            <motion.div
              initial={{ scale:0.9, y:20 }} animate={{ scale:1, y:0 }} exit={{ scale:0.9, y:20 }}
              transition={{ type:'spring', stiffness:140, damping:18 }}
              style={{ background:'#1e293b', borderRadius:'24px', padding:'32px', maxWidth:'400px', width:'100%', textAlign:'center', border:'1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ width:'60px', height:'60px', borderRadius:'50%', background:'rgba(245,158,11,0.1)', border:'2px solid rgba(245,158,11,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <AlertTriangle style={{ width:'28px', height:'28px', color:'#f59e0b' }} />
              </div>
              <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:800, color:'#f1f5f9' }}>Atención</h2>
              <p style={{ margin:'0 0 24px', fontSize:'14px', color:'#94a3b8', lineHeight:1.6 }}>
                Confirmaste un ítem que no corresponde a esta aeronave. Revisá con más atención. El flujo se reiniciará.
              </p>
              <motion.button onClick={handleRestart} whileTap={{ scale:0.95 }}
                style={{ padding:'14px 32px', background:'linear-gradient(135deg,#f59e0b,#d97706)', color:'white', border:'none', borderRadius:'14px', fontWeight:700, fontSize:'15px', cursor:'pointer', fontFamily:'inherit' }}>
                Reiniciar Flow
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlowScreen;
